import { timeParse } from "d3";

/**
 * Retrieves historical market data from an API using Apollo GraphQL.
 * @param {object} _this - Context variables.
 * @param {object} options - Request parameters.
 * @param {object} options.params - GraphQL query.
 * @param {object} options.variables - Query parameters.
 * @param {object} options.source - Data source key.
 * @param {boolean} [addToday=true] - Whether to add today's data adjustments.
 * @param {boolean|array} [accumDataKeys=false] - Whether to accumulate data from multiple keys.
 * @returns {Promise<object|array>} - Processed historical data.
 */
export function getHistoricalData(
  _this,
  { params, variables, source },
  addToday = true,
  accumDataKeys = false
) {
  return _this.$apollo
    .query({
      query: params,
      variables: variables,
      fetchPolicy: "network-only"
    })
    .then(response => {
      const rawData = response?.data?.[source];
      if (!rawData) throw new Error("Error from getHistoricalData");
      let data = JSON.parse(JSON.stringify(rawData));
      if (Array.isArray(data)) {
        data.forEach(d => {
          d.date = timeParse("%Y-%m-%d")(d.observation_date);
          d.ma40 = d.ma_40;
        });
        if (_this.selectedAssetType !== 2) {
          data = deleteDataRepeatAndAfterDate(data);
        }
        const lastEntry = data[data.length - 1];
        lastEntry.high = Math.max(
          lastEntry.high,
          lastEntry.open,
          lastEntry.close
        );
        lastEntry.low = Math.min(
          lastEntry.low,
          lastEntry.open,
          lastEntry.close
        );

        if (addToday) {
          updateMissingData(data, lastEntry);
        }
      } else if (!accumDataKeys) {
        normalizeSingleDataEntry(data);
      }
      // Accumulate data from multiple keys
      if (Array.isArray(accumDataKeys) && !Array.isArray(data)) {
        data = JSON.parse(data);
        if (validateDataRepeat(accumDataKeys, data)) {
          accumDataKeys.push(data);
        }
      }
      if (typeof data === "string") {
        data = JSON.parse(data);
      }
      _this.faildGetHistoricalData = false;
      return data;
    });
}

/**
 * Retrieves a simplified version of historical market data.
 * @param {object} _this - Context variables.
 * @param {object} options - Request parameters.
 * @returns {Promise<array>} - Simplified historical data.
 */
export function getHistoricalDataSimplified(
  _this,
  { params, variables, source }
) {
  return _this.$apollo.query({ query: params, variables }).then(response => {
    let data = JSON.parse(JSON.stringify(response?.data?.[source]));
    data.forEach(d => {
      d.date = timeParse("%Y-%m-%d")(d.observation_date);
    });
    data = deleteDataRepeatAndAfterDate(data);
    const lastEntry = data[data.length - 1];
    lastEntry.high = Math.max(lastEntry.high, lastEntry.open, lastEntry.close);
    lastEntry.low = Math.min(lastEntry.low, lastEntry.open, lastEntry.close);
    return data;
  });
}

/**
 * Updates missing trend and moving average values for the last data entry.
 * @param {array} data - Historical data array.
 * @param {object} lastEntry - The last entry in the data array.
 */
function updateMissingData(data, lastEntry) {
  const previousEntry = data[data.length - 2];
  const fieldsToCheck = [
    "interp_trend_short",
    "interp_trend_medium",
    "interp_trend_long"
  ];

  fieldsToCheck.forEach(field => {
    if (lastEntry[field] == null) {
      lastEntry[field] = previousEntry?.[field];
    }
  });

  if (lastEntry.ma20 == null || lastEntry.ma40 == null) {
    lastEntry.ma20 = calculateMovingAverage(data, 20);
    lastEntry.ma40 = calculateMovingAverage(data, 40);
  }

  if (lastEntry["vol-ewma"] == null || lastEntry["vol-ewma"] === 0) {
    lastEntry["vol-ewma"] = previousEntry?.["vol-ewma"];
  }
}

/**
 * Normalizes single data entry values by ensuring numeric properties.
 * @param {object} data - Single data entry object.
 */
function normalizeSingleDataEntry(data) {
  ["close", "last_price", "last_close", "last", "high", "low", "open"].forEach(
    key => {
      if (data[key] != null) {
        data[key] = +data[key];
      }
    }
  );
}

/**
 * Validates if a data entry is not a duplicate before accumulating.
 * @param {array} dataKeys - Accumulated data keys.
 * @param {object} data - New data entry.
 * @returns {boolean} - True if the data entry is unique.
 */
function validateDataRepeat(dataKeys, data) {
  return dataKeys.every(
    obj => obj.continuous_contract !== data.continuous_contract
  );
}

/**
 * Calculates the moving average for the last `n` entries.
 * @param {array} data - Historical data array.
 * @param {number} n - Number of periods.
 * @returns {number} - Computed moving average.
 */
function calculateMovingAverage(data, n) {
  return data.slice(-n).reduce((sum, entry) => sum + (entry.close || 0), 0) / n;
}

/**
 * Delete data repeat with date and after current date and after 9pm Chicago time
 * @param {array} data - Historical data array.
 * @returns {array} Filtered data array.
 */
function deleteDataRepeatAndAfterDate(data) {
  const chicagoTZ = "America/Chicago";
  const currentDate = new Date();
  const chicagoCurrentDate = new Date(
    currentDate.toLocaleString("en-US", { timeZone: chicagoTZ })
  );

  let date = [];
  let newData = [];

  data.forEach(d => {
    const newDate = new Date(d.date);
    if (
      (!date.includes(+d.date) && +newDate <= +chicagoCurrentDate) ||
      (+newDate > +chicagoCurrentDate && chicagoCurrentDate.getHours() >= 20)
    ) {
      date.push(+d.date);
      newData.push(d);
    }
  });

  return newData;
}

// /**
//  * Retrieves keys from Redis data storage.
//  * @param {object} _this - Context variables.
//  * @param {object} options - Request parameters.
//  * @returns {Promise<object>} - Redis data keys.
//  */
// export function getRedisDataKeys(_this, { params, variables, source }) {
//   return _this.$apollo
//     .query({ query: params, variables, fetchPolicy: "network-only" })
//     .then(response => {
//       const data = response?.data?.[source];
//       if (!data) throw new Error("Error from getRedisDataKeys");
//       return data;
//     });
// }
